import type { FormContactUs } from './validationSchema';
import type { IChangeEvent } from '@rjsf/core';
import type { RJSFSchema, RJSFValidationError, UiSchema } from '@rjsf/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Modal,
  Select,
  Skeleton,
  TextField,
  useMediaQuery,
} from '@mui/material';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { observer } from 'mobx-react';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { Button } from '@components/index';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ManagerFillIcon } from '@icons/ManagerFIllIcon';
import { CloseIcon, TriangleIcon } from '@icons/index';
import type { InquiryCreateRq } from '@repositories/inquiryRepository/inquiry.types';
import { useAddInquiry, useGetProductInquiryForm } from '@queryHooks/useInquiry';
import { useGetProductPlanList, useGetSoftware } from '@queryHooks/useSoftware';
import { useGetUserProfile } from '@queryHooks/useUser';
import { EXTERNAL_MANAGER_EMAIL_REGEX } from '@utils/YupUtil';
import { formTemplates, formWidgets } from './contactUsFormWidgets';
import { convertFormSchema } from './convertFormSchema';
import { PHONE_NUMBER_REGEX, useFormContactUs } from './validationSchema';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  swId: string;
  providerId?: string;
}

const MAX_INQUIRY_LENGTH = 300;
const SKELETON_FIELD_COUNT = 5;
const SKELETON_FIELDS = Array.from({ length: SKELETON_FIELD_COUNT });

export const ContactUs = observer(({ isOpen, onClose, swId, providerId }: Props) => {
  const { t } = useTranslation();
  const {
    authStore,
    uiStore: { toastStore },
  } = useStore();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [isMemoOver, setIsMemoOver] = useState<boolean>(false);
  const [consentAgreed, setConsentAgreed] = useState<boolean>(false);
  const [dynamicFormData, setDynamicFormData] = useState<Record<string, unknown>>({});
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [inquiryText, setInquiryText] = useState<string>('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    setError,
    formState: { errors },
  } = useFormContactUs();
  const { data: software } = useGetSoftware(swId);
  const { data: user } = useGetUserProfile(authStore.id, { enabled: !!authStore.isLogin });
  const { mutate: addInquiry, isPending } = useAddInquiry();

  // 플랜 목록 조회
  const { data: plans } = useGetProductPlanList(swId, { enabled: isOpen && !!providerId });
  const hasPlan = !!(plans && plans.length > 0);

  // 판매자 도입 문의 폼 조회
  const { data: inquiryFormData, isLoading: isInquiryFormLoading } = useGetProductInquiryForm(
    { providerId, productId: swId, isLatest: true, size: 1 },
    { enabled: isOpen && !!providerId },
  );

  const inquiryForm = inquiryFormData?.content?.[0];
  const hasInquiryForm = !!inquiryForm;

  const convertedForm = useMemo(() => {
    if (!inquiryForm?.formSchema) return null;
    return convertFormSchema(inquiryForm.formSchema, t);
  }, [inquiryForm?.formSchema, t]);

  const formSchema: RJSFSchema | null = convertedForm?.schema ?? null;
  const formUiSchema: UiSchema = useMemo(
    () => ({
      ...(convertedForm?.uiSchema ?? {}),
      'ui:submitButtonOptions': { norender: true },
    }),
    [convertedForm],
  );
  const fieldHelpTexts = useMemo(() => convertedForm?.fieldHelpTexts ?? {}, [convertedForm]);

  // validation 에러 메시지를 helpText로 교체
  const transformErrors = useCallback(
    (errors: RJSFValidationError[]) =>
      errors.map(error => {
        const fieldName = error.property?.replace(/^\./, '');
        if (fieldName && fieldHelpTexts[fieldName]) {
          return { ...error, message: fieldHelpTexts[fieldName] };
        }
        return error;
      }),
    [fieldHelpTexts],
  );

  // 기존 폼 제출 (폼이 없는 경우)
  const handleDefaultFormSubmit = (data: FormContactUs) => {
    if (!software) return;

    const req: InquiryCreateRq = {
      name: user ? user.name : data.name,
      email: user ? user.email : data.email,
      phone: user ? user.phone : data.phone,
      companyName: data.companyName,
      inquiry: data.inquiry,
      softwareId: software.id,
      consentAgreed: consentAgreed || undefined,
    };
    addInquiry(req, {
      onSuccess: () => {
        toastStore.open(t('Contact_Us.Success_Message'));
        onClose();
      },
    });
  };

  // 제출 버튼 클릭 시 기본 필드 유효성 검사 (RJSF 유효성 검사와 동시에 실행)
  const validateDynamicFormFields = () => {
    const companyNameValue = getValues('companyName');
    const nameValue = getValues('name');
    const emailValue = getValues('email');
    const phoneValue = getValues('phone');
    if (!companyNameValue) {
      setError('companyName', { message: t('Validation.Required_Field') });
    }
    if (!nameValue) {
      setError('name', { message: t('Member_Group_Delete_05') });
    }
    if (!emailValue) {
      setError('email', { message: t('Member_Group_Delete_05') });
    } else if (!EXTERNAL_MANAGER_EMAIL_REGEX.test(emailValue)) {
      setError('email', { message: t('Member_Manager_17') });
    }
    if (!phoneValue || !phoneValue.trim()) {
      setError('phone', { message: t('Validation.Required_Field') });
    } else if (!PHONE_NUMBER_REGEX.test(phoneValue)) {
      setError('phone', { message: t('Validation.Numbers_Only') });
    }
  };

  // 동적 폼 제출 (폼이 있는 경우) - RJSF 유효성 검사 통과 후 호출됨
  const handleDynamicFormSubmit = (data: IChangeEvent<Record<string, unknown>, RJSFSchema>) => {
    if (!inquiryForm || !software) return;

    const companyNameValue = getValues('companyName');
    const nameValue = getValues('name');
    const emailValue = getValues('email');
    const phoneValue = getValues('phone');

    // 기본 필드 유효성 검사 (빈 값 + 형식 검사)
    const isEmailValid = !!emailValue && EXTERNAL_MANAGER_EMAIL_REGEX.test(emailValue);
    const isPhoneValid = !!phoneValue && !!phoneValue.trim() && PHONE_NUMBER_REGEX.test(phoneValue);
    if (!companyNameValue || !nameValue || !isEmailValid || !isPhoneValid) return;

    if (!consentAgreed) {
      toastStore.open(t('Service_Request_10'));
      return;
    }

    const rawInquiry = data.formData?.inquiry;
    const inquiry = inquiryText || (typeof rawInquiry === 'string' ? rawInquiry : '');
    const req: InquiryCreateRq = {
      name: nameValue,
      email: emailValue,
      phone: phoneValue,
      companyName: companyNameValue,
      inquiry,
      softwareId: software.id,
      planId: selectedPlanId || undefined,
      formId: inquiryForm.id,
      formData: JSON.stringify(data.formData),
      consentAgreed,
    };
    addInquiry(req, {
      onSuccess: () => {
        toastStore.open(t('Contact_Us.Success_Message'));
        onClose();
      },
      onError: () => {
        toastStore.open(t('Contact_Us.Error_Message'));
      },
    });
  };

  const handleChangeTextArea = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    if (value.length > MAX_INQUIRY_LENGTH) {
      setValue('inquiry', value.slice(0, MAX_INQUIRY_LENGTH));
      setIsMemoOver(true);
    } else {
      setIsMemoOver(false);
    }
  };

  const handleDynamicInquiryChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setInquiryText(value.length <= MAX_INQUIRY_LENGTH ? value : value.slice(0, MAX_INQUIRY_LENGTH));
  }, []);

  useEffect(() => {
    reset();
    setDynamicFormData({});
    setConsentAgreed(false);
    setSelectedPlanId('');
    setInquiryText('');
  }, [isOpen, reset]);

  // 폼 로딩 스켈레톤
  const isFormLoading = !!providerId && isInquiryFormLoading;

  if (isFormLoading) {
    return (
      <Modal open={isOpen} onClose={onClose} sx={{ '& .MuiBackdrop-root': { background: 'transparent' } }}>
        <ModalFormWrapper as="div">
          <TitleContainer>
            <strong>{t('Product_Contact.Title')}</strong>
            <IconButton onClick={onClose} className="mobile-close-btn">
              <CloseIcon width={24} height={24} />
            </IconButton>
          </TitleContainer>
          <BodyContainer>
            {SKELETON_FIELDS.map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <SkeletonFieldGroup key={`skeleton-${index}`}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="rounded" width="100%" height={40} />
              </SkeletonFieldGroup>
            ))}
          </BodyContainer>
        </ModalFormWrapper>
      </Modal>
    );
  }

  // 동적 폼 렌더링 (RJSF)
  if (hasInquiryForm && formSchema && software) {
    return (
      <Modal open={isOpen} onClose={onClose} sx={{ '& .MuiBackdrop-root': { background: 'transparent' } }}>
        <ModalFormWrapper as="div">
          <TitleContainer>
            <strong>{t('Product_Contact.Title')}</strong>
            <IconButton onClick={onClose} className="mobile-close-btn">
              <CloseIcon width={24} height={24} />
            </IconButton>
          </TitleContainer>
          <BodyContainer>
            <Explanation>
              <ManagerFillIcon width={16} height={16} color={theme.colors['ic-primary-light']} />
              <ExplanationText>{t('Product_Contact.Contact_Notice')}</ExplanationText>
            </Explanation>
            <SubTitle>
              {t('Contact_Us.Product_Name')}
              <span>*</span>
            </SubTitle>
            <SoftwareName>{software.name}</SoftwareName>
            {hasPlan && (
              <>
                <SubTitle>{t('Subscrib_Main_12')}</SubTitle>
                <PlanSelect
                  size="small"
                  fullWidth
                  displayEmpty
                  value={selectedPlanId}
                  IconComponent={PlanSelectTriangleIcon}
                  onChange={e => setSelectedPlanId(String(e.target.value))}
                  renderValue={value =>
                    value ? (
                      <span>{plans?.find(plan => plan.planId === value)?.planName}</span>
                    ) : (
                      <PlaceholderText>{t('Product_Use_Request_02')}</PlaceholderText>
                    )
                  }
                >
                  {plans?.map(plan => (
                    <MenuItem key={plan.planId} value={plan.planId}>
                      {plan.planName}
                    </MenuItem>
                  ))}
                </PlanSelect>
              </>
            )}
            <SubTitle>
              {t('Contact_Us.Company_Name')}
              <span>*</span>
            </SubTitle>
            <InquiryField
              className={errors.companyName && 'form-error'}
              {...register('companyName')}
              size="medium"
              placeholder={t('Product_Inquiry_08')}
              error={!!errors.companyName?.message}
              helperText={errors.companyName?.message}
            />
            <SubTitle>
              {t('Contact_Us.Manager_Name')}
              <span>*</span>
            </SubTitle>
            <InquiryField
              className={errors.name && 'form-error'}
              {...register('name')}
              size="medium"
              placeholder={t('GNB_Profile_MyProfile_14')}
              error={!!errors.name?.message}
              helperText={errors.name?.message}
            />
            <SubTitle>
              {t('Contact_Us.Company_Email')}
              <span>*</span>
            </SubTitle>
            <InquiryField
              className={errors.email && 'form-error'}
              {...register('email')}
              size="medium"
              placeholder={t('Product_Inquiry_09')}
              error={!!errors.email?.message}
              helperText={errors.email?.message}
            />
            <SubTitle>
              {t('Member_User_16')}
              <span>*</span>
            </SubTitle>
            <InquiryField
              className={errors.phone && 'form-error'}
              {...register('phone')}
              size="medium"
              placeholder={t('Product_Inquiry_10')}
              error={!!errors.phone?.message}
              helperText={errors.phone?.message}
            />
            <DynamicFormWrapper>
              <Form
                id="dynamic-inquiry-form"
                schema={formSchema}
                uiSchema={formUiSchema}
                templates={formTemplates}
                widgets={formWidgets}
                validator={validator}
                formData={dynamicFormData}
                onChange={e => setDynamicFormData(e.formData ?? {})}
                onSubmit={handleDynamicFormSubmit}
                transformErrors={transformErrors}
                liveValidate={false}
                showErrorList={false}
                noHtml5Validate
              >
                <DynamicInquiryBox>
                  <SubTitle>{t('Contact_Us.Inquiry_Content')}</SubTitle>
                  <DynamicInquiryTextField
                    value={inquiryText}
                    onChange={handleDynamicInquiryChange}
                    placeholder={t('Contact_Us.Inquiry_Placeholder')}
                  />
                  <InquiryCount>
                    {inquiryText.length} <span>/ {MAX_INQUIRY_LENGTH}</span>
                  </InquiryCount>
                </DynamicInquiryBox>
                <ConsentWrapper>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={consentAgreed}
                        onChange={e => setConsentAgreed(e.target.checked)}
                        size="small"
                        sx={{
                          color: theme.colors['ic-primary'],
                          '&.Mui-checked': { color: theme.colors['ic-primary'] },
                        }}
                      />
                    }
                    label={t('Service_Request_10')}
                  />
                </ConsentWrapper>
              </Form>
            </DynamicFormWrapper>
          </BodyContainer>
          <ButtonContainer>
            <SubmitButton
              variant="contain"
              size="extraLarge"
              type="submit"
              form="dynamic-inquiry-form"
              isPending={isPending}
              disabled={!consentAgreed}
              onClick={validateDynamicFormFields}
            >
              {t('Contact_Us.Complete')}
            </SubmitButton>
            <CloseButton variant={matches ? 'contain' : 'outline'} size="extraLarge" onClick={onClose}>
              {t('Subscrib_Detail_BuyRequest_13')}
            </CloseButton>
          </ButtonContainer>
        </ModalFormWrapper>
      </Modal>
    );
  }

  // 기존 폼 렌더링
  return (
    <Modal open={isOpen} onClose={onClose} sx={{ '& .MuiBackdrop-root': { background: 'transparent' } }}>
      <ModalFormWrapper onSubmit={handleSubmit(handleDefaultFormSubmit)}>
        <TitleContainer>
          <strong>{t('Product_Contact.Title')}</strong>
          <IconButton onClick={onClose} className="mobile-close-btn">
            <CloseIcon width={24} height={24} />
          </IconButton>
        </TitleContainer>
        <BodyContainer height={!authStore.isLogin ? '770px' : undefined}>
          <Explanation>
            <ManagerFillIcon width={16} height={16} color={theme.colors['ic-primary-light']} />
            <ExplanationText>{t('Product_Contact.Contact_Notice')}</ExplanationText>
          </Explanation>
          <SubTitle>
            {t('Contact_Us.Product_Name')}
            <span>*</span>
          </SubTitle>
          <SoftwareName>{software.name}</SoftwareName>
          {!authStore.isLogin && (
            <>
              <SubTitle>
                {t('Contact_Us.Company_Name')}
                <span>*</span>
              </SubTitle>
              <InquiryField
                className={errors.companyName && 'form-error'}
                {...register('companyName')}
                size="medium"
                placeholder={t('Product_Inquiry_08')}
                error={!!errors.companyName?.message}
                helperText={errors.companyName?.message}
              />
              <SubTitle>
                {t('Contact_Us.Manager_Name')}
                <span>*</span>
              </SubTitle>
              <InquiryField
                className={errors.name && 'form-error'}
                {...register('name')}
                size="medium"
                placeholder={t('GNB_Profile_MyProfile_14')}
                error={!!errors.name?.message}
                helperText={errors.name?.message}
              />
              <SubTitle>
                {t('Contact_Us.Company_Email')}
                <span>*</span>
              </SubTitle>
              <InquiryField
                className={errors.email && 'form-error'}
                {...register('email')}
                size="medium"
                placeholder={t('Product_Inquiry_09')}
                error={!!errors.email?.message}
                helperText={errors.email?.message}
              />
              <SubTitle>
                {t('Member_User_16')}
                <span>*</span>
              </SubTitle>
              <InquiryField
                className={errors.phone && 'form-error'}
                {...register('phone')}
                size="medium"
                placeholder={t('Product_Inquiry_10')}
                error={!!errors.phone?.message}
                helperText={errors.phone?.message}
              />
            </>
          )}
          <InquiryBox>
            <SubTitle>
              {t('Contact_Us.Inquiry_Content')}
              <span>*</span>
            </SubTitle>
            <InquiryTextField
              {...register('inquiry', { onChange: handleChangeTextArea })}
              placeholder={t('Contact_Us.Inquiry_Placeholder')}
              isMemoOver={isMemoOver}
              isLogin={authStore.isLogin}
              error={!!errors.inquiry?.message}
            />
            <InquiryCount>
              {watch('inquiry') ? watch('inquiry').length : 0} <span>/ {MAX_INQUIRY_LENGTH}</span>
            </InquiryCount>
          </InquiryBox>
          {errors.inquiry?.message && <ErrorText>{errors.inquiry?.message}</ErrorText>}
        </BodyContainer>
        <ButtonContainer>
          <SubmitButton variant="contain" size="extraLarge" type="submit" isPending={isPending}>
            {t('Contact_Us.Complete')}
          </SubmitButton>
          <CloseButton variant={matches ? 'contain' : 'outline'} size="extraLarge" onClick={onClose}>
            {t('Subscrib_Detail_BuyRequest_13')}
          </CloseButton>
        </ButtonContainer>
      </ModalFormWrapper>
    </Modal>
  );
});

const ModalFormWrapper = styled.form`
  width: 480px;
  max-height: calc(100vh - 40px);
  position: fixed;
  right: 20px;
  bottom: 17px;
  transform: translate(0, 0);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 6px 30px 5px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    max-height: 100vh;
  }
`;
const TitleContainer = styled(Box)`
  padding: 18px 28px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  & strong {
    ${fonts.Headline6};
  }
`;
const BodyContainer = styled(Box)`
  padding: 4px 28px 30px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    height: 100%;
    padding: 12px 24px 24px;
  }
`;
const ButtonContainer = styled(Box)`
  display: flex;
  margin-top: 30px;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    position: relative;
    margin-top: 0;
    padding: 0;
    gap: 0;
    &::before {
      position: absolute;
      top: -16px;
      left: 0;
      content: '';
      display: block;
      width: 100%;
      height: 16px;
      background: ${({ theme }) => theme.colors['bg-w-gradient']};
    }
  }
`;
const SubTitle = styled('strong')`
  ${fonts.Headline8};
  margin-bottom: 10px;
  & span {
    margin-left: 2px;
    color: ${({ theme }) => theme.colors['text-red-light']};
  }
`;
const SoftwareName = styled('div')`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-light']};
  padding: 9px 12px;
  border-radius: 2px;
  margin-bottom: 16px;
  ${fonts.Body2};
`;

const InquiryField = styled(TextField)`
  ${fonts.Body2}
  margin-bottom: 16px;

  & > div {
    height: 40px;
    flex-shrink: 0;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors['text-gray-light']};
  }

  &.form-error {
    margin-bottom: 60px;
  }
`;

const InquiryTextField = styled.textarea<{ isMemoOver: boolean; isLogin: boolean; error: boolean }>`
  ${fonts.Body2}
  width: 100%;
  padding: 6px 12px;
  height: ${({ isLogin }) => (isLogin ? '240px' : '160px')};
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  resize: none;
  border: 1px solid
    ${({ theme, isMemoOver, error }) =>
      isMemoOver || error ? theme.colors['border-red-light'] : theme.colors['border-gray-lighter']};
  border-radius: 2px;
`;

const IconButton = styled(Button)`
  width: 24px;
  height: 24px;
  ${({ theme }) => theme.breakpoints.between('xxs', 'lg')} {
    display: none;
    &.mobile-close-btn {
      width: 28px;
      height: 28px;
      display: flex;
      margin-left: auto;
      margin-right: 0;
    }
  }
`;

const SubmitButton = styled(SecureSubmitButton)`
  flex: 1;
  height: 54px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  border-radius: 0;
  border-bottom-left-radius: 6px;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    width: 50%;
    height: 54px;
  }
  &:hover {
    background: ${({ theme: { colors } }) => colors['state-gradient-hover']};
  }
`;
const CloseButton = styled(Button)`
  flex: 1;
  height: 54px;
  background: ${({ theme }) => theme.colors['bg-gray-light']};
  border-radius: 0;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-bottom-right-radius: 6px;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    width: 50%;
    height: 54px;
  }
`;

const Explanation = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  align-items: center;
  justify-content: flex-start;
  flex-direction: initial;
  gap: 6px;
  background-color: ${({ theme }) => theme.colors['bg-primary-lighter']};
  border-radius: 3px;
  padding: 12px 20px;
`;

const ExplanationText = styled.p`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-primary']};
`;

const InquiryBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const InquiryCount = styled.span`
  ${fonts.Body4};
  position: absolute;
  right: 10px;
  bottom: 5px;
  margin: 0;
  span {
    color: ${({ theme }) => theme.colors['text-gray-light']};
  }
`;

const ErrorText = styled.span`
  ${fonts.Body4}
  color: ${({ theme }) => theme.colors['text-red-light']};
  margin-top: 4px;
`;

const DynamicFormWrapper = styled.div`
  /* RJSF Grid 기본 padding 제거 (InquiryField와 간격 통일) */
  & .MuiGrid-item {
    padding-top: 0 !important;
  }

  /* 필드 간격 */
  & .MuiFormControl-root {
    position: relative;
    margin-top: 0;
    margin-bottom: 8px;
  }

  /* 라벨: static 위치, floating 애니메이션 제거 */
  & .MuiInputLabel-root {
    ${fonts.Headline8};
    position: static !important;
    transform: none !important;
    padding: 0 0 10px 0;
    color: ${({ theme }) => theme.colors['text-gray-main']} !important;
    pointer-events: none;

    /* required 필드 별표 스타일 */
    & .MuiInputLabel-asterisk {
      color: ${({ theme }) => theme.colors['text-red-light']};
    }
  }

  /* Input: 라벨 공간 제거, placeholder 세로 중앙 */
  & .MuiInputBase-root {
    margin-top: 0;
    padding: 0;
  }
  & .MuiOutlinedInput-root {
    height: 40px;
    & .MuiOutlinedInput-notchedOutline {
      top: 0;
      legend {
        display: none;
      }
    }
  }
  & .MuiInputBase-input {
    padding: 0 12px;
    height: 40px;

    /* placeholder 항상 표시 */
    &::placeholder {
      opacity: 1;
    }

    /* number input 스피너 제거 */
    &[type='number'] {
      -moz-appearance: textfield;
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  /* Select 필드 */
  & .MuiSelect-select {
    padding: 0px 12px !important;
    height: 40px !important;
    display: flex;
    align-items: center;
  }

  /* BOOLEAN 체크박스 필드 스타일 */
  & .MuiFormControlLabel-root {
    margin-left: 0;
    margin-right: 0;
    & .MuiFormControlLabel-label {
      ${fonts.Body2};
    }
    & .MuiCheckbox-root {
      padding: 4px 8px 4px 0;
      color: ${({ theme }) => theme.colors['ic-primary']};
      &.Mui-checked {
        color: ${({ theme }) => theme.colors['ic-primary']};
      }
    }
  }

  /* MuiFormHelperText 숨김 (helpText는 FieldErrorTemplate으로 표시) */
  & .MuiFormHelperText-root {
    display: none;
  }
  /* 에러 상태 input 빨간 border */
  & .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-red-light']};
  }
  /* RJSF FieldErrorTemplate 에러 메시지 */
  & .MuiList-root {
    padding: 0;
    margin: 4px 0 0;
    & .MuiListItem-root {
      padding: 0;
      ${fonts.Body4};
      color: ${({ theme }) => theme.colors['text-red-light']};
    }
  }
`;

const SkeletonFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const PlanSelect = styled(Select)`
  margin-bottom: 16px;
  height: 40px;
  & .MuiSelect-select {
    ${fonts.Body2};
    height: 40px !important;
    display: flex;
    align-items: center;
  }
  & .MuiOutlinedInput-notchedOutline {
    top: 0;
    padding: 0;
    legend {
      display: none;
    }
  }
`;

const PlanSelectTriangleIcon = (props: JSX.Element) => (
  <PlanSelectTriangleIconStyled
    width={16}
    height={16}
    rotateNum={180}
    className="MuiSvgIcon-root MuiSelect-icon"
    {...props}
  />
);

const PlanSelectTriangleIconStyled = styled(TriangleIcon)`
  top: calc(50% - 8px) !important;
  right: 9px !important;
  &.MuiSelect-iconOpen {
    transform: rotate(0) !important;
  }
`;

const PlaceholderText = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const DynamicInquiryBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;

const DynamicInquiryTextField = styled.textarea`
  ${fonts.Body2}
  width: 100%;
  padding: 6px 12px;
  height: 120px;
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  resize: none;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 2px;
`;

const ConsentWrapper = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 0 10px;
  & .MuiFormControlLabel-label {
    ${fonts.Body2};
  }
`;
